import { formatPrice } from './priceHelpers';

// Import all locale files for PDF translation
import en from '../locales/en.json';
import ar from '../locales/ar.json';
import de from '../locales/de.json';
import es from '../locales/es.json';
import fr from '../locales/fr.json';
import it from '../locales/it.json';
import ja from '../locales/ja.json';
import ko from '../locales/ko.json';
import pt from '../locales/pt.json';
import ru from '../locales/ru.json';
import tr from '../locales/tr.json';
import zh from '../locales/zh.json';

const locales = { en, ar, de, es, fr, it, ja, ko, pt, ru, tr, zh };

// Get PDF translations for a specific language
function getPdfTranslations(language = 'en') {
  const locale = locales[language] || locales.en;
  const pdf = locale.pdf || {};

  return {
    buyingListReport: pdf.buyingListReport || 'Buying List Report',
    user: pdf.user || 'User',
    listName: pdf.listName || 'List Name',
    discount: pdf.discount || 'Discount',
    generated: pdf.generated || 'Generated',
    totalValue: pdf.totalValue || 'Total Value',
    avgPerUnit: pdf.avgPerUnit || 'Avg / Unit',
    quantity: pdf.quantity || 'Quantity',
    weight: pdf.weight || 'Weight',
    code: pdf.code || 'Code',
    brand: pdf.brand || 'Brand',
    pricePerUnit: pdf.pricePerUnit || 'Price / Unit',
    qty: pdf.qty || 'Qty',
    total: pdf.total || 'Total',
    noItemsInList: pdf.noItemsInList || 'No items in this list.',
    categoryBreakdown: pdf.categoryBreakdown || 'Category Breakdown',
    pgmPerOunce: pdf.pgmPerOunce || 'PGM / Ounce',
    pricePerKgMetalsAnalysis: pdf.pricePerKgMetalsAnalysis || 'Price per KG & Metals Analysis',
    pricePerKg: pdf.pricePerKg || 'Price/kg',
    generatedBy: pdf.generatedByNaim || 'Generated by Naim App',
    metalsAnalysis: pdf.metalsAnalysis || 'Metals Analysis',
    discounted: pdf.discounted || 'Discounted',
  };
}

export function generateBuyingListPDF({
  items,
  listName,
  currency,
  discountPct,
  userName,
  totalValue,
  avgUnit,
  totalQty,
  ceramicWeightKg,
  pricePerKg,
  metals,
  boardsMetals,
  boardMode = false,
  dailyPrices,
  computePrice,
  isAdmin,
  showWeight = true,
  companyName = 'Gebrüder Naim',
  language = 'en',
}) {
  try {
    // Load translations for selected language
    const t = getPdfTranslations(language);

    const disc = Number(discountPct) || 0;
    const discMul = (100 - disc) / 100;
    const discountedAmount = totalValue * Math.abs(disc) / 100;

    // Generate table rows
    const rowsHtml = items.map((it) => {
      const unitBase = computePrice(it);
      const unit = typeof unitBase === 'number' ? unitBase * discMul : null;
      const qty = Number(it.total) || 1;
      const lineTotal = typeof unit === 'number' ? unit * qty : 0;
      const unitStr = typeof unit === 'number' ? formatPrice(unit, currency) : '-';
      const totalStr = typeof unit === 'number' ? formatPrice(lineTotal, currency) : '-';
      const code = String(it.code || '-').split(',')[0]?.trim() || '-';
      const brand = String(it.brand || '-');

      return `
        <tr>
          <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;">${code}</td>
          <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;">${brand}</td>
          <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;text-align:right;">${unitStr}</td>
          <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;text-align:center;">${qty}</td>
          <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;text-align:right;">${totalStr}</td>
        </tr>
      `;
    }).join('');

    const weightKgStr = ceramicWeightKg.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const now = new Date();
    const generatedAt = now.toLocaleString();
    const dateForFilename = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

    // Category counts - dynamic based on actual item categories/materials
    const categoryMap = {};
    for (const it of items) {
      const cat = String(it.category || it.material || 'Other').trim();
      if (cat) {
        const displayCat = cat.charAt(0).toUpperCase() + cat.slice(1).toLowerCase();
        categoryMap[displayCat] = (categoryMap[displayCat] || 0) + (Number(it.total) || 1);
      }
    }
    const bucketLabels = Object.keys(categoryMap).sort();
    const bucketMap = categoryMap;

    // PGM per-ounce prices
    const curKey = String(currency || 'USD').slice(-3).toLowerCase() + 'USD';
    const rate = Number(dailyPrices?.[curKey]) || 1;
    const platPerOz = Number(dailyPrices?.platUSD) * rate || 0;
    const pallPerOz = Number(dailyPrices?.pallUSD) * rate || 0;
    const rhodPerOz = Number(dailyPrices?.rhodUSD) * rate || 0;

    // RTL support for Arabic
    const isRTL = language === 'ar';
    const dirAttr = isRTL ? 'dir="rtl"' : '';

    const html = `
      <!DOCTYPE html>
      <html ${dirAttr}>
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${listName || 'Buying List'}</title>
          <style>
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
              color: #1f2937;
              background: #ffffff;
              padding: 24px;
              font-size: 12px;
              line-height: 1.5;
              ${isRTL ? 'direction: rtl; text-align: right;' : ''}
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              padding-bottom: 12px;
              border-bottom: 3px solid #047857;
            }
            .header h1 {
              font-size: 22px;
              font-weight: 700;
              color: #047857;
              margin-bottom: 2px;
            }
            .header .subtitle {
              font-size: 13px;
              color: #6b7280;
            }
            .info-grid {
              display: flex;
              flex-wrap: wrap;
              gap: 8px;
              margin-bottom: 16px;
              padding: 12px;
              background: #f8fafc;
              border-radius: 8px;
              border: 1px solid #e5e7eb;
            }
            .info-item {
              flex: 1;
              min-width: 120px;
            }
            .info-label {
              font-size: 10px;
              color: #6b7280;
              text-transform: uppercase;
              font-weight: 600;
              letter-spacing: 0.5px;
            }
            .info-value {
              font-size: 13px;
              font-weight: 600;
              color: #1f2937;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 16px;
              background: #ffffff;
            }
            th {
              background: #047857;
              color: #ffffff;
              text-align: ${isRTL ? 'right' : 'left'};
              padding: 10px 12px;
              font-size: 11px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            th:nth-child(3), th:nth-child(5) {
              text-align: ${isRTL ? 'left' : 'right'};
            }
            th:nth-child(4) {
              text-align: center;
            }
            td {
              vertical-align: middle;
              font-size: 12px;
            }
            tr:nth-child(even) {
              background: #f9fafb;
            }
            .summary-box {
              background: linear-gradient(135deg, #065f46 0%, #10b981 100%);
              color: #ffffff;
              border-radius: 8px;
              padding: 16px;
              margin-bottom: 16px;
              display: flex;
              flex-wrap: wrap;
              justify-content: space-around;
              gap: 12px;
            }
            .summary-item {
              text-align: center;
              min-width: 80px;
            }
            .summary-value {
              font-size: 18px;
              font-weight: 700;
              margin-bottom: 2px;
            }
            .summary-label {
              font-size: 10px;
              opacity: 0.9;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .section-title {
              font-size: 13px;
              font-weight: 600;
              color: #047857;
              margin-bottom: 10px;
              padding-bottom: 6px;
              border-bottom: 1px solid #e5e7eb;
              text-align: start;
            }
            .categories-grid {
              display: flex;
              flex-wrap: wrap;
              gap: 8px;
              margin-bottom: 16px;
            }
            .category-card {
              background: #f8fafc;
              border: 1px solid #e5e7eb;
              border-radius: 6px;
              padding: 10px 16px;
              text-align: center;
              min-width: 70px;
            }
            .category-count {
              font-size: 18px;
              font-weight: 700;
              color: #047857;
            }
            .category-label {
              font-size: 10px;
              color: #6b7280;
              margin-top: 2px;
            }
            .pgm-grid {
              display: flex;
              flex-direction: row;
              flex-wrap: wrap;
              gap: 6px;
              margin-bottom: 16px;
              direction: ltr;
            }
            .pgm-card {
              background: #fef3c7;
              border: 1px solid #f59e0b;
              border-radius: 6px;
              padding: 6px 10px;
              text-align: center;
              flex: 1;
              min-width: 70px;
              max-width: 90px;
            }
            .pgm-value {
              font-size: 11px;
              font-weight: 700;
              color: #92400e;
            }
            .pgm-label {
              font-size: 9px;
              color: #b45309;
              margin-top: 1px;
            }
            .metals-section {
              display: flex;
              flex-direction: row;
              flex-wrap: wrap;
              gap: 6px;
              direction: ltr;
            }
            .metal-card {
              border-radius: 6px;
              padding: 6px 10px;
              text-align: center;
              flex: 1;
              min-width: 60px;
              max-width: 80px;
            }
            .metal-card.pt { background: rgba(79, 70, 229, 0.15); border: 1px solid #4F46E5; }
            .metal-card.pt .metal-value, .metal-card.pt .metal-label { color: #4F46E5; }
            .metal-card.pd { background: rgba(6, 182, 212, 0.15); border: 1px solid #06B6D4; }
            .metal-card.pd .metal-value, .metal-card.pd .metal-label { color: #06B6D4; }
            .metal-card.rh { background: rgba(139, 92, 246, 0.15); border: 1px solid #8B5CF6; }
            .metal-card.rh .metal-value, .metal-card.rh .metal-label { color: #8B5CF6; }
            /* Boards metals: AU=yellow, AG=gray, CU=orange */
            .metal-card.au { background: rgba(234, 179, 8, 0.15); border: 1px solid #EAB308; }
            .metal-card.au .metal-value, .metal-card.au .metal-label { color: #B45309; }
            .metal-card.ag { background: rgba(148, 163, 184, 0.15); border: 1px solid #a5d6a7; }
            .metal-card.ag .metal-value, .metal-card.ag .metal-label { color: #81c784; }
            .metal-card.cu { background: rgba(249, 115, 22, 0.15); border: 1px solid #F97316; }
            .metal-card.cu .metal-value, .metal-card.cu .metal-label { color: #C2410C; }
            .metal-value { font-size: 11px; font-weight: 700; }
            .metal-label { font-size: 9px; margin-top: 1px; font-weight: 600; }
            .footer {
              text-align: center;
              padding-top: 12px;
              border-top: 1px solid #e5e7eb;
              color: #9ca3af;
              font-size: 10px;
            }
            @media print {
              body { padding: 12px; }
              .summary-box { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              th { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${companyName}</h1>
            <div class="subtitle">${t.buyingListReport}</div>
          </div>

          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">${t.user}</div>
              <div class="info-value">${userName || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">${t.listName}</div>
              <div class="info-value">${listName || 'Default'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">${t.discount}</div>
              <div class="info-value">${discountPct}%</div>
            </div>
            <div class="info-item">
              <div class="info-label">${t.generated}</div>
              <div class="info-value">${generatedAt}</div>
            </div>
          </div>

          <div class="summary-box">
            <div class="summary-item">
              <div class="summary-value">${formatPrice(totalValue, currency)}</div>
              <div class="summary-label">${t.totalValue}</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">${formatPrice(avgUnit, currency)}</div>
              <div class="summary-label">${t.avgPerUnit}</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">${totalQty}</div>
              <div class="summary-label">${t.quantity}</div>
            </div>
            ${showWeight ? `
            <div class="summary-item">
              <div class="summary-value">${weightKgStr} kg</div>
              <div class="summary-label">${t.weight}</div>
            </div>
            ` : ''}
            ${disc !== 0 ? `
            <div class="summary-item">
              <div class="summary-value">${formatPrice(discountedAmount, currency)}</div>
              <div class="summary-label">${t.discounted}</div>
            </div>
            ` : ''}
          </div>

          ${((isAdmin && metals && !boardMode) || (isAdmin && boardsMetals && boardMode)) || (dailyPrices && (platPerOz > 0 || pallPerOz > 0 || rhodPerOz > 0)) ? `
          <div style="display:flex;gap:16px;flex-wrap:wrap;margin-bottom:16px;">
            ${isAdmin && boardsMetals && boardMode ? `
              <div style="min-width:180px;">
                <div class="section-title">${t.metalsAnalysis || 'Metals Analysis'}</div>
                <div class="metals-section">
                  <div class="metal-card au">
                    <div class="metal-value">${boardsMetals.au}</div>
                    <div class="metal-label">AU</div>
                  </div>
                  <div class="metal-card ag">
                    <div class="metal-value">${boardsMetals.ag}</div>
                    <div class="metal-label">AG</div>
                  </div>
                  <div class="metal-card pd">
                    <div class="metal-value">${boardsMetals.pd}</div>
                    <div class="metal-label">PD</div>
                  </div>
                  <div class="metal-card cu">
                    <div class="metal-value">${boardsMetals.cu}</div>
                    <div class="metal-label">CU</div>
                  </div>
                </div>
              </div>
            ` : ''}
            ${isAdmin && metals && !boardMode ? `
              <div style="min-width:140px;">
                <div class="section-title">${t.metalsAnalysis || 'Metals Analysis'}</div>
                <div class="metals-section">
                  <div class="metal-card pt">
                    <div class="metal-value">${metals.pt}</div>
                    <div class="metal-label">PT</div>
                  </div>
                  <div class="metal-card pd">
                    <div class="metal-value">${metals.pd}</div>
                    <div class="metal-label">PD</div>
                  </div>
                  <div class="metal-card rh">
                    <div class="metal-value">${metals.rh}</div>
                    <div class="metal-label">RH</div>
                  </div>
                </div>
              </div>
            ` : ''}
            ${dailyPrices && (platPerOz > 0 || pallPerOz > 0 || rhodPerOz > 0) ? `
              <div style="min-width:180px;">
                <div class="section-title">${t.pgmPerOunce} (${currency})</div>
                <div class="pgm-grid">
                  <div class="pgm-card">
                    <div class="pgm-value">${formatPrice(platPerOz, currency)}</div>
                    <div class="pgm-label">PT</div>
                  </div>
                  <div class="pgm-card">
                    <div class="pgm-value">${formatPrice(pallPerOz, currency)}</div>
                    <div class="pgm-label">PD</div>
                  </div>
                  <div class="pgm-card">
                    <div class="pgm-value">${formatPrice(rhodPerOz, currency)}</div>
                    <div class="pgm-label">RH</div>
                  </div>
                </div>
              </div>
            ` : ''}
          </div>
          ` : ''}

          <table>
            <thead>
              <tr>
                <th>${t.code}</th>
                <th>${t.brand}</th>
                <th>${t.pricePerUnit}</th>
                <th>${t.qty}</th>
                <th>${t.total}</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml || `<tr><td colspan="5" style="padding:20px;text-align:center;color:#9ca3af;">${t.noItemsInList}</td></tr>`}
            </tbody>
          </table>

          <div style="display:flex;gap:16px;flex-wrap:wrap;margin-bottom:16px;">
            <div style="flex:1;min-width:200px;">
              <div class="section-title">${t.categoryBreakdown}</div>
              <div class="categories-grid">
                ${bucketLabels.map(label => `
                  <div class="category-card">
                    <div class="category-count">${bucketMap[label]}</div>
                    <div class="category-label">${label}</div>
                  </div>
                `).join('')}
              </div>
            </div>
          </div>

          <div class="footer">
            ${t.generatedBy} - ${generatedAt}
          </div>
        </body>
      </html>
    `;

    const w = window.open('', '_blank');
    if (!w) {
      alert('Unable to open print window. Please check your browser settings.');
      return null;
    }

    // Set the document title for PDF filename: "CompanyName - Date - ListName"
    const pdfFilename = `${companyName} - ${dateForFilename} - ${listName || 'Buying List'}`;

    w.document.open();
    w.document.write(html);
    w.document.title = pdfFilename;
    w.document.close();

    setTimeout(() => {
      w.focus();
      w.print();
    }, 100);

    return pdfFilename;
  } catch (e) {
    console.error('PDF generation error:', e);
    alert('Unable to generate PDF. Please try again.');
    return null;
  }
}
